import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { Field } from '../../../components/Field.jsx';
import { LIST_PROVEEDORES } from '../Proveedores/gql.js';
import { CREATE_ORDEN_COMPRA, UPDATE_ORDEN_COMPRA } from './gql.js';

const emptyItem = () => ({
  descripcion: '',
  cantidad: 1,
  precioUnit: 0,
  dtoPorc: 0,
  productoId: '',
});

const initialState = {
  proveedorId: '',
  fecha: new Date().toISOString().slice(0, 10),
  estado: 'BORRADOR',
  observacion: '',
};

export default function OrdenCompraForm({ initialData, onCancel, onSaved }) {
  const isEdit = Boolean(initialData?.id);
  const [formState, setFormState] = useState(initialState);
  const [items, setItems] = useState([emptyItem()]);
  const [errors, setErrors] = useState({});
  const [, createOrden] = useMutation(CREATE_ORDEN_COMPRA);
  const [, updateOrden] = useMutation(UPDATE_ORDEN_COMPRA);
  const [{ data: proveedoresData }] = useQuery({
    query: LIST_PROVEEDORES,
    variables: { pagination: { limit: 100, offset: 0 } },
  });

  useEffect(() => {
    if (initialData) {
      setFormState({
        proveedorId: initialData.proveedorId ?? '',
        fecha: initialData.fecha ?? new Date().toISOString().slice(0, 10),
        estado: initialData.estado ?? 'BORRADOR',
        observacion: initialData.observacion ?? '',
      });
      setItems(
        initialData.items?.length
          ? initialData.items.map((item) => ({
              descripcion: item.descripcion ?? '',
              cantidad: Number(item.cantidad ?? 1),
              precioUnit: Number(item.precioUnit ?? 0),
              dtoPorc: Number(item.dtoPorc ?? 0),
              productoId: item.productoId ?? '',
            }))
          : [emptyItem()],
      );
    } else {
      setFormState(initialState);
      setItems([emptyItem()]);
    }
  }, [initialData]);

  const proveedorOptions = useMemo(
    () => proveedoresData?.listProveedores?.items ?? [],
    [proveedoresData],
  );

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: field === 'descripcion' ? value : Number(value),
      };
      return next;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const total = useMemo(() => {
    return items.reduce((acc, item) => {
      const cantidad = Number(item.cantidad) || 0;
      const precio = Number(item.precioUnit) || 0;
      const dto = Number(item.dtoPorc) || 0;
      const subtotal = cantidad * precio * (1 - dto / 100);
      return acc + subtotal;
    }, 0);
  }, [items]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!formState.proveedorId) {
      nextErrors.proveedorId = 'Seleccioná un proveedor.';
    }
    if (!formState.fecha) {
      nextErrors.fecha = 'La fecha es obligatoria.';
    }
    const validItems = items.filter((item) => item.descripcion.trim());
    if (!validItems.length) {
      nextErrors.items = 'Agregá al menos un ítem con descripción.';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const input = {
      proveedorId: String(formState.proveedorId),
      fecha: formState.fecha,
      observacion: formState.observacion.trim() || null,
      estado: formState.estado,
      items: validItems.map((item) => ({
        productoId: item.productoId ? String(item.productoId) : null,
        descripcion: item.descripcion.trim(),
        cantidad: Number(item.cantidad) || 0,
        precioUnit: Number(item.precioUnit) || 0,
        dtoPorc: Number(item.dtoPorc) || 0,
      })),
    };

    try {
      if (isEdit) {
        await updateOrden({ id: initialData.id, input });
      } else {
        await createOrden({ input });
      }
      onSaved?.();
    } catch (error) {
      setErrors({ form: error.message });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title={isEdit ? 'Editar orden de compra' : 'Nueva orden de compra'}
        subtitle="Definí los productos y condiciones de compra antes de enviar la orden al proveedor."
      />

      {errors.form ? (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{errors.form}</div>
      ) : null}

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <Field label="Proveedor" hint={errors.proveedorId}>
            <select
              name="proveedorId"
              value={formState.proveedorId}
              onChange={(event) => setFormState((prev) => ({ ...prev, proveedorId: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Seleccioná proveedor…</option>
              {proveedorOptions.map((prov) => (
                <option key={prov.id} value={prov.id}>
                  {prov.razonSocial}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fecha" hint={errors.fecha}>
            <input
              type="date"
              name="fecha"
              value={formState.fecha}
              onChange={(event) => setFormState((prev) => ({ ...prev, fecha: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Estado">
            <select
              name="estado"
              value={formState.estado}
              onChange={(event) => setFormState((prev) => ({ ...prev, estado: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="BORRADOR">Borrador</option>
              <option value="APROBADA">Aprobada</option>
              <option value="ANULADA">Anulada</option>
            </select>
          </Field>
          <Field label="Observación">
            <textarea
              name="observacion"
              value={formState.observacion}
              onChange={(event) => setFormState((prev) => ({ ...prev, observacion: event.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Ítems</h2>
            {errors.items ? <p className="text-sm text-red-300">{errors.items}</p> : null}
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-3 py-1.5 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Agregar ítem
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {items.map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-12 gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)] p-4"
            >
              <div className="col-span-12 md:col-span-4">
                <label className="mb-1 block text-xs font-medium text-muted">Descripción</label>
                <input
                  value={item.descripcion}
                  onChange={(event) => handleItemChange(index, 'descripcion', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Cantidad</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.cantidad}
                  onChange={(event) => handleItemChange(index, 'cantidad', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Precio unitario</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.precioUnit}
                  onChange={(event) => handleItemChange(index, 'precioUnit', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Descuento %</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={item.dtoPorc}
                  onChange={(event) => handleItemChange(index, 'dtoPorc', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-1 flex items-end">
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-200 transition hover:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  disabled={items.length === 1}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-end">
          <p className="text-sm font-semibold text-[rgb(var(--fg))]">
            Total estimado:{' '}
            {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(total)}
          </p>
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            {isEdit ? 'Guardar cambios' : 'Crear orden'}
          </button>
        }
      />
    </form>
  );
}
